import { z } from 'zod';

/**
 * Terminfinder: Umfragen mit mehreren Terminvorschlägen pro Kampagne.
 * Gespeichert wird in UTC, angezeigt in der Zeitzone der Runde (Europe/Berlin).
 */

export const POLL_TIME_ZONE = 'Europe/Berlin';

export const pollAnswerSchema = z.enum(['yes', 'maybe', 'no']);
export type PollAnswer = z.infer<typeof pollAnswerSchema>;

export type PollStatus = 'open' | 'closed' | 'fixed' | 'cancelled';

/** Punkte je Antwort fürs Ranking. */
export const ANSWER_POINTS: Record<PollAnswer, number> = { yes: 2, maybe: 1, no: 0 };

/** Antippen wechselt Ja → Vielleicht → Nein → Ja (unbeantwortet beginnt bei Ja). */
export function nextAnswer(a: PollAnswer | null | undefined): PollAnswer {
  return a === 'yes' ? 'maybe' : a === 'maybe' ? 'no' : 'yes';
}

// ---------- Zeitzone ----------

const partsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: POLL_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function zoneParts(ms: number): { y: number; mo: number; d: number; h: number; mi: number; s: number } {
  const p = Object.fromEntries(partsFormat.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: +p.year!, mo: +p.month!, d: +p.day!, h: +p.hour!, mi: +p.minute!, s: +p.second! };
}

/** Abstand der Zone zu UTC in Millisekunden zum Zeitpunkt `ms` (Sommerzeit: +2 h). */
function zoneOffset(ms: number): number {
  const p = zoneParts(ms);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

/**
 * Ortszeit der Runde (`2026-10-24`, `19:00`) → UTC-Zeitpunkt (ISO).
 * Zeiten in der Umstellungslücke (z. B. 02:30 im März) rutschen eine Stunde vor.
 */
export function zonedToUtc(date: string, time: string): string {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!dm || !tm) throw new Error(`Ungültiges Datum/Uhrzeit: ${date} ${time}`);
  const local = Date.UTC(+dm[1]!, +dm[2]! - 1, +dm[3]!, +tm[1]!, +tm[2]!);
  let utc = local - zoneOffset(local);
  // Zweiter Durchlauf: Offset am tatsächlichen Zeitpunkt (Tage mit Zeitumstellung)
  utc = local - zoneOffset(utc);
  return new Date(utc).toISOString();
}

/** UTC-Zeitpunkt → Datum (`YYYY-MM-DD`) und Uhrzeit (`HH:MM`) in der Zone der Runde. */
export function utcToZoned(iso: string): { date: string; time: string } {
  const p = zoneParts(new Date(iso).getTime());
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${p.y}-${pad(p.mo)}-${pad(p.d)}`, time: `${pad(p.h)}:${pad(p.mi)}` };
}

/** Anzeige, z. B. „Sa., 24.10.2026, 19:00“ (bzw. mit Ende „19:00–23:00“). */
export function formatSlot(startAt: string, endAt?: string | null, opts: { year?: boolean } = {}): string {
  const date = new Intl.DateTimeFormat('de-DE', {
    timeZone: POLL_TIME_ZONE,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    ...(opts.year === false ? {} : { year: 'numeric' }),
  }).format(new Date(startAt));
  const start = utcToZoned(startAt).time;
  return endAt ? `${date}, ${start}–${utcToZoned(endAt).time}` : `${date}, ${start}`;
}

// ---------- Ranking ----------

export interface RankSlot {
  id: string;
  startAt: string;
}
export interface RankParticipant {
  id: string;
  isRequired: boolean;
}
export interface RankVote {
  participantId: string;
  slotId: string;
  answer: PollAnswer;
}

export interface SlotStats {
  slotId: string;
  yes: number;
  maybe: number;
  no: number;
  /** Noch keine Antwort. */
  open: number;
  score: number;
  quorumMet: boolean;
  /** Ein Pflichtteilnehmer hat Nein gesagt – der Termin ist raus. */
  blocked: boolean;
  /** 1 = bester Termin. */
  rank: number;
}

/**
 * Bewertet alle Termine: zuerst erfülltes Quorum, dann Punkte (Ja 2, Vielleicht 1),
 * dann Anzahl Ja, bei Gleichstand der frühere Termin. Blockierte Termine stehen am Ende.
 */
export function rankSlots(
  slots: readonly RankSlot[],
  participants: readonly RankParticipant[],
  votes: readonly RankVote[],
  rules: { minYes: number; maybeCounts: boolean },
): SlotStats[] {
  const required = new Set(participants.filter((p) => p.isRequired).map((p) => p.id));
  const known = new Set(participants.map((p) => p.id));
  const stats = slots.map((s): SlotStats & { startAt: string } => {
    const vs = votes.filter((v) => v.slotId === s.id && known.has(v.participantId));
    const yes = vs.filter((v) => v.answer === 'yes').length;
    const maybe = vs.filter((v) => v.answer === 'maybe').length;
    const no = vs.length - yes - maybe;
    const blocked = vs.some((v) => v.answer === 'no' && required.has(v.participantId));
    const counted = yes + (rules.maybeCounts ? maybe : 0);
    return {
      slotId: s.id,
      startAt: s.startAt,
      yes,
      maybe,
      no,
      open: known.size - vs.length,
      score: vs.reduce((sum, v) => sum + ANSWER_POINTS[v.answer], 0),
      quorumMet: !blocked && counted >= rules.minYes,
      blocked,
      rank: 0,
    };
  });
  stats.sort(
    (a, b) =>
      Number(a.blocked) - Number(b.blocked) ||
      Number(b.quorumMet) - Number(a.quorumMet) ||
      b.score - a.score ||
      b.yes - a.yes ||
      a.startAt.localeCompare(b.startAt),
  );
  return stats.map(({ startAt: _s, ...rest }, i) => ({ ...rest, rank: i + 1 }));
}

/** Darf noch abgestimmt werden? (offen und Deadline nicht überschritten) */
export function pollAcceptsVotes(p: { status: PollStatus; deadline: string | null }, now = new Date()): boolean {
  return p.status === 'open' && (!p.deadline || new Date(p.deadline) > now);
}

/** Angezeigter Status: Eine offene Umfrage mit abgelaufener Deadline gilt als geschlossen. */
export function effectivePollStatus(p: { status: PollStatus; deadline: string | null }, now = new Date()): PollStatus {
  return p.status === 'open' && p.deadline && new Date(p.deadline) <= now ? 'closed' : p.status;
}

// ---------- Eingaben ----------

const isoSchema = z.string().datetime({ offset: true });

export const pollSlotInputSchema = z
  .object({ startAt: isoSchema, endAt: isoSchema.nullable().default(null) })
  .refine((s) => !s.endAt || new Date(s.endAt) > new Date(s.startAt), { message: 'Ende muss nach dem Beginn liegen', path: ['endAt'] });

export const pollParticipantInputSchema = z.object({
  displayName: z.string().trim().min(1).max(60),
  userId: z.string().uuid().nullable().default(null),
  isRequired: z.boolean().default(false),
});

const pollSettings = {
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000),
  locationHint: z.string().trim().max(200),
  deadline: isoSchema.nullable(),
  /** Mindestanzahl Zusagen (inkl. DM). */
  minYes: z.number().int().min(0).max(50),
  /** Zählt „vielleicht“ fürs Quorum mit? */
  maybeCounts: z.boolean(),
  /** So viele Tage nach dem festgelegten Termin bleiben die Links gültig (Kalender-Download). */
  linkDays: z.number().int().min(0).max(90),
};

export const createPollSchema = z.object({
  title: pollSettings.title,
  description: pollSettings.description.default(''),
  locationHint: pollSettings.locationHint.default(''),
  deadline: pollSettings.deadline.default(null),
  minYes: pollSettings.minYes.default(4),
  maybeCounts: pollSettings.maybeCounts.default(false),
  linkDays: pollSettings.linkDays.default(7),
  slots: z.array(pollSlotInputSchema).min(2, 'Mindestens zwei Terminvorschläge').max(60),
  participants: z.array(pollParticipantInputSchema).max(50).default([]),
  /** Den anlegenden DM als Pflichtteilnehmer aufnehmen. */
  includeMe: z.boolean().default(true),
});
export type CreatePollInput = z.input<typeof createPollSchema>;

export const updatePollSchema = z
  .object({
    ...pollSettings,
    /** Schließen, wieder öffnen oder abbrechen; „festgelegt“ nur über `finalize`. */
    status: z.enum(['open', 'closed', 'cancelled']),
  })
  .partial();
export type UpdatePollInput = z.input<typeof updatePollSchema>;

export const updateParticipantSchema = z.object({ displayName: z.string().trim().min(1).max(60), isRequired: z.boolean() }).partial();

export const castVoteSchema = z.object({
  answer: pollAnswerSchema,
  comment: z.string().trim().max(300).default(''),
});

/** DM trägt eine Antwort für jemanden ein (z. B. per Messenger erhalten); `null` löscht sie. */
export const setVoteSchema = z.object({
  participantId: z.string().uuid(),
  slotId: z.string().uuid(),
  answer: pollAnswerSchema.nullable(),
  comment: z.string().trim().max(300).optional(),
});

export const finalizePollSchema = z.object({ slotId: z.string().uuid() });

// ---------- Antworten ----------

export interface PollSlotDTO {
  id: string;
  startAt: string;
  endAt: string | null;
}

export interface PollParticipantDTO {
  id: string;
  displayName: string;
  userId: string | null;
  isRequired: boolean;
  /** Persönlicher Link (`/poll/<token>`); null = widerrufen. */
  link: string | null;
}

export interface PollVoteDTO {
  participantId: string;
  slotId: string;
  answer: PollAnswer;
  comment: string;
  updatedAt: string;
}

export interface PollDTO {
  id: string;
  title: string;
  description: string;
  locationHint: string;
  deadline: string | null;
  /** Gespeicherter Status (siehe `effectivePollStatus` für die Anzeige). */
  status: PollStatus;
  minYes: number;
  maybeCounts: boolean;
  linkDays: number;
  finalSlotId: string | null;
  sessionNoteId: string | null;
  createdAt: string;
  slots: PollSlotDTO[];
  participants: PollParticipantDTO[];
  votes: PollVoteDTO[];
  ranking: SlotStats[];
}

export interface PollListEntry {
  id: string;
  title: string;
  status: PollStatus;
  deadline: string | null;
  slotCount: number;
  participantCount: number;
  /** Teilnehmer, die mindestens einen Termin beantwortet haben. */
  answeredCount: number;
  final: PollSlotDTO | null;
  /** Bin ich (angemeldet) Teilnehmer? Dann kann ich in der App abstimmen. */
  mine: boolean;
}

/** Sicht eines Teilnehmers (Token-Link oder angemeldet): nur Umfrage, eigene Antworten und Summen. */
export interface BallotDTO {
  campaignName: string;
  title: string;
  description: string;
  locationHint: string;
  deadline: string | null;
  status: PollStatus;
  canVote: boolean;
  displayName: string;
  slots: (PollSlotDTO & { yes: number; maybe: number })[];
  mine: Record<string, { answer: PollAnswer; comment: string }>;
  final: PollSlotDTO | null;
}

export interface ScheduleDTO {
  next: { pollId: string; title: string; startAt: string; endAt: string | null; sessionNoteId: string | null } | null;
  /** Offene Umfragen (Spieler: nur eigene, die noch nicht vollständig beantwortet sind). */
  openPolls: number;
}
